import React, { useState, useEffect, useCallback, useRef } from 'react';

var API = 'https://api.algrok.com';
var POLL_LIVE_MS = 1000;
var POLL_PRE_MS = 60000;
var WATCHDOG_MS = 30000;
var SPARKLINE_SAMPLE_MS = 60000;

function fmtRs(n) {
  if (n === null || n === undefined || isNaN(n)) return '0';
  var v = Math.round(n);
  var s = Math.abs(v).toLocaleString('en-IN');
  return v < 0 ? '−' + s : (v > 0 ? '+' + s : '0');
}
function fmtRsLarge(n) {
  if (n === null || n === undefined || isNaN(n)) return '₹0';
  var v = Math.round(n);
  var s = Math.abs(v).toLocaleString('en-IN');
  return v < 0 ? '−₹' + s : (v > 0 ? '+₹' + s : '₹0');
}
function fmtTime(ts) {
  if (!ts) return '';
  try {
    if (typeof ts === 'number') ts = new Date(ts * 1000).toISOString();
    var d = new Date(ts);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch (e) { return ''; }
}
function todayKey() {
  var d = new Date();
  var ist = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return ist.getFullYear() + '-' + String(ist.getMonth() + 1).padStart(2, '0') + '-' + String(ist.getDate()).padStart(2, '0');
}

function getIstParts() {
  var now = new Date();
  var ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return { day: ist.getDay(), hour: ist.getHours(), min: ist.getMinutes(), sec: ist.getSeconds(), minutesNow: ist.getHours() * 60 + ist.getMinutes() };
}
function getMarketState() {
  var p = getIstParts();
  if (p.day === 0 || p.day === 6) return 'WEEKEND';
  if (p.minutesNow < 555) return 'PRE_MARKET';      // 9:15
  if (p.minutesNow >= 930) return 'POST_MARKET';    // 15:30
  return 'OPEN';
}
function getPollInterval(state) {
  if (state === 'OPEN') return POLL_LIVE_MS;
  if (state === 'PRE_MARKET') return POLL_PRE_MS;
  return null;
}
function formatTimeToOpen() {
  var p = getIstParts();
  if (p.day === 0 || p.day === 6) return 'Reopens Mon 09:15';
  var diff = 555 - p.minutesNow;
  if (diff <= 0) return '';
  if (diff > 60) {
    var h = Math.floor(diff / 60);
    var m = diff % 60;
    return 'Opens in ' + h + 'h ' + m + 'm';
  }
  if (diff > 5) return 'Opens in ' + diff + 'm';
  var secsLeft = diff * 60 - p.sec;
  if (secsLeft < 30) return 'Opens shortly';
  return 'Opens in ' + Math.floor(secsLeft / 60) + 'm ' + (secsLeft % 60) + 's';
}

function adaptAmbLike(state, id, label) {
  if (!state || state.error) return null;
  var realized = state.realized_pnl || 0;
  var unrealized = state.unrealized_pnl || 0;
  var mtm = realized + unrealized;
  var legs = state.legs || [];
  var hadPositionToday = state.active || realized !== 0 || legs.length > 0;
  if (!hadPositionToday) return null;
  var entryTime = null;
  if (legs.length > 0 && legs[0].entry_time) entryTime = legs[0].entry_time;
  else if (state.position_entry_time) entryTime = state.position_entry_time;
  var squareoffTime = null;
  if (!state.active && state.events) {
    var lastExit = [].concat(state.events).reverse().find(function(e) { return e.type === 'EXIT' || e.kind === 'exit'; });
    if (lastExit) squareoffTime = lastExit.time || lastExit.ts;
  }
  return { id: id, label: label, mtm: mtm, status: state.active ? 'LIVE' : 'CLOSED', positions: legs.length, entry_time: entryTime, squareoff_time: squareoffTime };
}
function adaptQuantiply(algos) {
  if (!Array.isArray(algos) || algos.length === 0) return null;
  var todayAlgos = algos.filter(function(a) {
    var hasLiveLegs = (a.legs_detail || []).some(function(l) { return Math.abs(l.qty || 0) > 0; });
    return a.isToday || hasLiveLegs;
  });
  if (todayAlgos.length === 0) return null;
  var totalMtm = todayAlgos.reduce(function(s, a) { return s + (a.mtm || 0); }, 0);
  var liveLegCount = 0;
  var anyActive = false;
  todayAlgos.forEach(function(a) {
    (a.legs_detail || []).forEach(function(l) {
      // Must check STATUS, not qty. A closed leg keeps its qty as a record of
      // what was traded, so counting qty alone left the tile reading LIVE for
      // the rest of the day after an exit — while /algos correctly reported
      // status=trade_closed with zero open legs.
      if (l.status === 'open' && Math.abs(l.qty || 0) > 0) { liveLegCount++; anyActive = true; }
    });
  });
  return { id: 'quantiply', label: 'QUANTIPLY', mtm: totalMtm, status: anyActive ? 'LIVE' : 'CLOSED', positions: liveLegCount, entry_time: null, squareoff_time: null };
}

function tileColors(strat, maxAbs) {
  if (strat.status === 'CLOSED') {
    return {
      bg: 'rgba(40,52,75,0.55)', border: 'rgba(60,75,100,0.6)',
      label: '#8899aa', mtm: strat.mtm >= 0 ? '#86efac' : '#fca5a5',
      sub: 'rgba(136,153,170,0.7)', pillText: '#647084',
    };
  }
  var intensity = maxAbs > 0 ? Math.abs(strat.mtm) / maxAbs : 0;
  var opacity = 0.20 + 0.50 * Math.min(1, intensity);
  var borderOp = Math.min(0.85, opacity + 0.15);
  if (strat.mtm >= 0) {
    var lc = opacity > 0.55 ? '#f0fdf4' : opacity > 0.40 ? '#ecfdf5' : '#d1fae5';
    return { bg: 'rgba(16,185,129,' + opacity.toFixed(2) + ')', border: 'rgba(16,185,129,' + borderOp.toFixed(2) + ')', label: lc, mtm: lc, sub: 'rgba(240,253,244,0.75)', pillText: lc };
  } else {
    var lr = opacity > 0.55 ? '#fef2f2' : opacity > 0.40 ? '#fee2e2' : '#fca5a5';
    return { bg: 'rgba(239,68,68,' + opacity.toFixed(2) + ')', border: 'rgba(239,68,68,' + borderOp.toFixed(2) + ')', label: lr, mtm: lr, sub: 'rgba(254,242,242,0.75)', pillText: lr };
  }
}

function loadSparkline() {
  try {
    var raw = localStorage.getItem('algrok_dashboard_sparkline');
    if (!raw) return [];
    var data = JSON.parse(raw);
    if (data.date !== todayKey()) { localStorage.removeItem('algrok_dashboard_sparkline'); return []; }
    return data.points || [];
  } catch (e) { return []; }
}
function saveSparkline(points) {
  try { localStorage.setItem('algrok_dashboard_sparkline', JSON.stringify({ date: todayKey(), points: points })); } catch (e) {}
}

export default function DashboardM() {
  var [strategies, setStrategies] = useState([]);
  var [totalMtm, setTotalMtm] = useState(0);
  var [errorLog, setErrorLog] = useState([]);
  var [sparkline, setSparkline] = useState(loadSparkline());
  var [marketState, setMarketState] = useState(getMarketState());
  var [, forceTick] = useState(0);
  var sparkRef = useRef(0);

  var STRATS = [
    { id: 'amb', label: 'AMB', endpoint: '/amb-state' },
    { id: 'amb2', label: 'AMB 2', endpoint: '/amb2-state' },
    { id: 'amb3', label: 'AMB 3', endpoint: '/amb3-state' },
    { id: 'super_straddle', label: 'SS 1', endpoint: '/super-straddle/state' },
    { id: 'super_straddle_2', label: 'SS 2', endpoint: '/super-straddle-2/state' },
    { id: 'super_straddle_3', label: 'SS 3', endpoint: '/super-straddle-3/state' },
    { id: 'oiwalls_live', label: 'WALLS L', endpoint: '/oiwalls-live/state' },
    { id: 'oiwalls3x_live', label: 'W3x L', endpoint: '/oiwalls3x-live/state' },
    { id: 'gexwalls_live', label: 'GWALLS L', endpoint: '/gexwalls-live/state' },
    { id: 'oiride_d_live', label: 'ORIDE D L', endpoint: '/oiride-d-live/state' },
    { id: 'oiride_r_live', label: 'OIRIDE R L', endpoint: '/oiride-r-live/state' },
    { id: 'directional_tv', label: 'DIR TV', endpoint: '/directional-tv/state' },
    { id: 'directional_tv1', label: 'DIR TV1', endpoint: '/directional-tv-1/state' },
  ];

  var fetchAll = useCallback(function() {
    var errs = [];
    var promises = STRATS.map(function(s) {
      return fetch(API + s.endpoint)
        .then(function(r) { return r.json(); })
        .then(function(d) {
          // Errors are collected BEFORE the tile filter — a strategy that failed
          // to enter has no legs and no pnl, so its tile is hidden, but that is
          // exactly when you most need to see the error.
          if (d && !d.error) {
            (d.events || []).forEach(function(e) {
              // WARNINGS as well as errors — a strategy that cannot see the
              // option chain is just as stuck as one whose order was rejected.
              var lvl = e.type || e.level || '';
              if (lvl !== 'error' && lvl !== 'warning') return;
              errs.push({ label: s.label, time: e.ts || e.time || '', msg: e.msg || e.text || '', lvl: lvl });
            });
          }
          return adaptAmbLike(d, s.id, s.label);
        })
        .catch(function() { return null; });
    });
    promises.push(fetch(API + '/algos').then(function(r) { return r.json(); }).then(function(d) { return adaptQuantiply(d); }).catch(function() { return null; }));

    Promise.all(promises).then(function(results) {
      var alive = results.filter(function(s) { return s !== null; });
      var total = alive.reduce(function(s, x) { return s + x.mtm; }, 0);
      setStrategies(alive);
      setTotalMtm(total);
      // Errors first so a warning burst cannot push a real one off the list.
      errs.sort(function(a, b) {
        var ra = a.lvl === 'error' ? 0 : 1;
        var rb = b.lvl === 'error' ? 0 : 1;
        if (ra !== rb) return ra - rb;
        return String(b.time).localeCompare(String(a.time));
      });
      setErrorLog(errs.slice(0, 40));

      if (getMarketState() === 'OPEN') {
        var now = Date.now();
        if (now - sparkRef.current >= SPARKLINE_SAMPLE_MS) {
          sparkRef.current = now;
          setSparkline(function(prev) {
            var next = prev.concat([{ t: now, m: total }]);
            if (next.length > 600) next = next.slice(-600);
            saveSparkline(next);
            return next;
          });
        }
      }
    });
  }, []);

  // Watchdog
  useEffect(function() {
    var id = setInterval(function() {
      var newState = getMarketState();
      setMarketState(function(prev) { return newState !== prev ? newState : prev; });
      forceTick(function(n) { return n + 1; });
    }, WATCHDOG_MS);
    return function() { clearInterval(id); };
  }, []);

  // Poller
  useEffect(function() {
    fetchAll();
    var pollMs = getPollInterval(marketState);
    if (pollMs === null) return;
    var id = setInterval(fetchAll, pollMs);
    return function() { clearInterval(id); };
  }, [marketState, fetchAll]);

  var navigate = function(pageId) {
    window.dispatchEvent(new CustomEvent('navigate-to', { detail: pageId }));
  };

  var sorted = strategies.slice().sort(function(a, b) {
    if (a.status !== b.status) return a.status === 'LIVE' ? -1 : 1;
    return b.mtm - a.mtm;
  });

  var topGainId = null, topLossId = null;
  if (sorted.length > 0) {
    var maxStrat = sorted.reduce(function(p, c) { return c.mtm > p.mtm ? c : p; });
    var minStrat = sorted.reduce(function(p, c) { return c.mtm < p.mtm ? c : p; });
    if (maxStrat.mtm > 0) topGainId = maxStrat.id;
    if (minStrat.mtm < 0) topLossId = minStrat.id;
  }

  var maxAbs = sorted.reduce(function(m, s) { return Math.max(m, Math.abs(s.mtm)); }, 0);
  var liveCount = sorted.filter(function(s) { return s.status === 'LIVE'; }).length;
  var closedCount = sorted.length - liveCount;
  var totalColor = totalMtm > 0 ? '#10b981' : totalMtm < 0 ? '#ef4444' : '#8899aa';

  // Status
  var status;
  if (marketState === 'OPEN') status = { dot: '#10b981', label: '● LIVE', sub: '1s', pulse: true };
  else if (marketState === 'PRE_MARKET') status = { dot: '#fbbf24', label: '○ PRE', sub: formatTimeToOpen(), pulse: false };
  else if (marketState === 'POST_MARKET') status = { dot: '#647084', label: '○ EOD', sub: '15:30 IST', pulse: false };
  else status = { dot: '#647084', label: '○ WKND', sub: formatTimeToOpen(), pulse: false };

  var sparkPath = '';
  if (sparkline.length >= 2) {
    var minM = Math.min.apply(null, sparkline.map(function(p) { return p.m; }));
    var maxM = Math.max.apply(null, sparkline.map(function(p) { return p.m; }));
    var range = maxM - minM || 1;
    var sw = 290, sh = 28;
    sparkline.forEach(function(p, i) {
      var x = (i / (sparkline.length - 1)) * sw;
      var y = sh - 3 - ((p.m - minM) / range) * (sh - 6);
      sparkPath += (i === 0 ? 'M' : 'L') + x.toFixed(1) + ',' + y.toFixed(1) + ' ';
    });
  }

  return (
    <div style={{ padding: '12px 10px 30px', minHeight: '100%', color: '#e2e8f0', fontFamily: 'inherit' }}>

      {/* Header */}
      <div style={{ padding: '4px 6px 4px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: '#4a6080', letterSpacing: 1.2 }}>PORTFOLIO P&amp;L · DAY</div>
          <div style={{ fontSize: 34, color: totalColor, fontWeight: 700, letterSpacing: -0.5, marginTop: 2, lineHeight: 1 }}>
            {fmtRsLarge(totalMtm)}
          </div>
          <div style={{ fontSize: 11, color: '#8899aa', marginTop: 4 }}>
            {sorted.length === 0
              ? (marketState === 'OPEN' ? 'No strategies engaged' : 'No strategies engaged')
              : sorted.length + ' engaged · ' + liveCount + ' live' + (closedCount > 0 ? ' · ' + closedCount + ' closed' : '')}
          </div>
        </div>
        <div style={{ textAlign: 'right', minWidth: 90 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: status.dot, boxShadow: status.pulse ? '0 0 6px ' + status.dot : 'none' }} />
            <span style={{ fontSize: 9, color: status.dot, fontWeight: 700, letterSpacing: 0.8 }}>{status.label}</span>
          </div>
          <div style={{ fontSize: 9, color: '#4a6080', marginTop: 3 }}>{status.sub}</div>
        </div>
      </div>

      {sparkPath && (
        <div style={{ padding: '6px 6px 4px' }}>
          <svg width="100%" height="28" viewBox="0 0 290 28" preserveAspectRatio="none">
            <path d={sparkPath} fill="none" stroke={totalColor} strokeWidth="1.5" />
          </svg>
        </div>
      )}

      <div style={{ height: 1, background: 'rgba(26,45,74,0.4)', margin: '8px 0 10px' }} />

      {sorted.length === 0 ? (
        <div style={{
          padding: '40px 20px', textAlign: 'center', color: '#4a6080',
          background: 'rgba(20,28,46,0.3)', border: '1px solid rgba(26,45,74,0.4)', borderRadius: 8, marginTop: 12,
        }}>
          <div style={{ fontSize: 13, marginBottom: 4 }}>
            {marketState === 'OPEN' ? 'No positions today' :
             marketState === 'PRE_MARKET' ? 'Market opens at 09:15' :
             marketState === 'POST_MARKET' ? 'Market closed' : 'Market closed'}
          </div>
          <div style={{ fontSize: 10 }}>
            {marketState === 'OPEN' ? 'Tiles will appear as strategies engage' : 'Resumes when market opens'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {sorted.map(function(s) {
            var c = tileColors(s, maxAbs);
            var isTopGain = s.id === topGainId;
            var isTopLoss = s.id === topLossId;
            return (
              <div
                key={s.id}
                onClick={function() { navigate(s.id); }}
                style={{
                  background: c.bg, border: '1px solid ' + c.border, borderRadius: 6,
                  padding: '10px 12px', cursor: 'pointer', position: 'relative',
                }}
              >
                {isTopGain && (
                  <div style={{
                    position: 'absolute', top: -6, right: 6, background: '#0ea5e9', color: '#05080f',
                    fontSize: 7, padding: '1px 5px', borderRadius: 3, fontWeight: 700, letterSpacing: 0.2,
                  }}>TOP GAIN</div>
                )}
                {isTopLoss && !isTopGain && (
                  <div style={{
                    position: 'absolute', top: -6, right: 6, background: '#a78bfa', color: '#05080f',
                    fontSize: 7, padding: '1px 5px', borderRadius: 3, fontWeight: 700, letterSpacing: 0.2,
                  }}>TOP LOSS</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 10.5, color: c.label, fontWeight: 700, letterSpacing: 0.3 }}>{s.label}</span>
                  <span style={{ fontSize: 7.5, color: c.pillText, fontWeight: 700, letterSpacing: 0.3 }}>
                    {s.status === 'LIVE' ? '● LIVE' : 'CLOSED'}
                  </span>
                </div>
                <div style={{ fontSize: 19, color: c.mtm, fontWeight: 700, margin: '4px 0 1px', letterSpacing: -0.3 }}>
                  {fmtRs(s.mtm)}
                </div>
                <div style={{ fontSize: 9, color: c.sub }}>
                  {s.status === 'LIVE'
                    ? (s.positions + ' pos')
                    : ('sq ' + (s.squareoff_time ? fmtTime(s.squareoff_time) : '—'))}
                </div>
              </div>
            );
          })}
        </div>
      )}


      {errorLog.length > 0 && (
        <div style={{
          marginTop: 12, background: 'rgba(239,68,68,0.05)',
          border: '1px solid rgba(239,68,68,0.28)', borderRadius: 8, padding: '10px 12px',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#f87171', letterSpacing: 0.6, marginBottom: 6 }}>
            ☠ ISSUES ({errorLog.filter(function(x) { return x.lvl === 'error'; }).length}E · {errorLog.filter(function(x) { return x.lvl !== 'error'; }).length}W)
          </div>
          <div style={{ maxHeight: 150, overflowY: 'auto' }}>
            {errorLog.map(function(e, i) {
              return (
                <div key={i} style={{ fontSize: 10, padding: '3px 0', borderBottom: i < errorLog.length - 1 ? '1px solid rgba(239,68,68,0.10)' : 'none' }}>
                  <span style={{ color: e.lvl === 'error' ? '#fca5a5' : '#fbbf24', fontWeight: 700 }}>{e.label}</span>
                  <span style={{ color: '#647084' }}> {e.time} </span>
                  <div style={{ color: e.lvl === 'error' ? '#e2e8f0' : '#c8b78a', wordBreak: 'break-word' }}>{e.msg}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}
